import { computed } from 'vue';
import { usePrintingStore } from '@/composition/use-printing';
import { usePrintHealthStore } from '@/stores/print-health.store';
import { isNativePrintingStrategy } from '@/composition/printing/printing-strategy-native';
import { PrintPluginStatus } from '@/modules/PrintService';
import { IPrinter } from '@/interfaces/IPrinter';
import { UnifiedPrinterStatus } from '@/lib/print/print-health.utils';

export interface IAssignedPrinterHealth {
  /** Health-map key (bridge: printer ref; native: '<type>:<id>'). */
  key: string;
  printer: IPrinter;
  status: UnifiedPrinterStatus;
}

/**
 * Unified assigned-printer health selector (parity core): joins the printing
 * store's assignments with the health map the ACTIVE strategy pushes into
 * the print-health store, regardless of platform. Consumed by the app-bar
 * widget (amber offline warning) and the recovery watcher in use-orders.
 *
 * Pure computeds — safe to call from multiple components.
 */
export function usePrintHealth() {
  const printingStore = usePrintingStore();
  const healthStore = usePrintHealthStore();

  /** Assigned printers (ticket + sticker + per-station comanda), deduped. */
  const assignedPrinters = computed<IPrinter[]>(() => {
    const byKey = new Map<string, IPrinter>();
    const add = (printer: IPrinter | null) => {
      if (printer) byKey.set(`${printer.type}:${printer.id}`, printer);
    };
    add(printingStore.ticketPrinter);
    add(printingStore.stickerPrinter);
    for (const stationKey of Object.keys(
      printingStore.comandaPrintersByStation
    )) {
      add(printingStore.comandaPrintersByStation[+stationKey]);
    }
    return [...byKey.values()];
  });

  const assignedPrinterHealth = computed<IAssignedPrinterHealth[]>(() => {
    // Reactive strategy read (getter, not a captured ref): desktop swaps
    // plugin → bridge strategy at runtime. While the strategy is not
    // connected there is no live health — the widget's red status dot is
    // the signal then, not per-printer amber.
    const strategy = printingStore.getStrategy();
    if (strategy.status.value !== PrintPluginStatus.Connected) return [];

    const isNative = isNativePrintingStrategy(strategy);
    return assignedPrinters.value.map((printer) => {
      const key = isNative
        ? `${printer.type}:${printer.id}`
        : String(printer.id);
      return {
        key,
        printer,
        status: healthStore.printerHealth[key] ?? 'unknown',
      };
    });
  });

  const offlineAssignedPrinters = computed(() =>
    assignedPrinterHealth.value.filter((entry) => entry.status === 'offline')
  );

  /** Plain status map keyed by health key (recovery-watcher diff input). */
  const assignedPrinterStatusMap = computed<
    Record<string, UnifiedPrinterStatus>
  >(() => {
    const map: Record<string, UnifiedPrinterStatus> = {};
    for (const entry of assignedPrinterHealth.value) {
      map[entry.key] = entry.status;
    }
    return map;
  });

  return {
    assignedPrinters,
    assignedPrinterHealth,
    offlineAssignedPrinters,
    assignedPrinterStatusMap,
  };
}
